'use client';
import { useEffect, useRef, useState } from 'react';

/**
 * Command palette (⌘K / Ctrl+K) — lista di comandi/link filtrabile, navigabile
 * da tastiera. Contenuto via props, nessun link hardcoded.
 * `onNavigate`: se passato (es. `navigate` di react-router-dom, da useNavigate())
 * viene chiamato con `item.href` per una navigazione SPA senza reload; se
 * omesso, fallback a `window.location.href` (nessun import di router qui).
 * @param {{ items: Array<{ id: string, label: string, href?: string, onSelect?: () => void, hint?: string }>, onNavigate?: (href: string) => void }} props
 */
export function CommandPalette({ items = [], onNavigate }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  const filtered = items.filter((it) => it.label.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    function onKeydown(e) {
      const isK = e.key === 'k' && (e.metaKey || e.ctrlKey);
      if (isK) { e.preventDefault(); setOpen((o) => !o); }
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  }, []);

  useEffect(() => {
    if (open) { setQuery(''); setActive(0); setTimeout(() => inputRef.current?.focus(), 0); }
  }, [open]);

  function select(item) {
    setOpen(false);
    if (item.onSelect) item.onSelect();
    else if (item.href) { if (onNavigate) onNavigate(item.href); else window.location.href = item.href; }
  }

  function onInputKeydown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter' && filtered[active]) select(filtered[active]);
  }

  // Focus trap: mentre il dialog è aperto, Tab non deve poter uscire dal
  // pannello (altrimenti il focus da tastiera finirebbe nella pagina sotto,
  // ancora visibile mentre il dialog è aperto).
  function onPanelKeydown(e) {
    if (e.key !== 'Tab') return;
    const focusables = panelRef.current?.querySelectorAll('input, button');
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  if (!open) return null;

  return (
    <div
      // Sopra 1000: i controlli di Leaflet (blocco Mappa) usano z-index:1000
      // di loro — un overlay con z-index più basso ci finisce sotto.
      className="fixed inset-0 z-[1100] flex items-start justify-center pt-[15vh] px-4"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={() => setOpen(false)}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Cerca"
        className="w-full max-w-lg rounded-xl border border-theme-border bg-theme-bg-surface shadow-vs-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onPanelKeydown}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setActive(0); }}
          onKeyDown={onInputKeydown}
          placeholder="Cerca..."
          className="w-full px-4 py-3.5 text-sm text-theme-text bg-transparent outline-none border-b border-theme-border"
        />
        <div className="max-h-72 overflow-y-auto py-1.5">
          {filtered.length === 0 && (
            <p className="px-4 py-3 text-sm text-theme-text-subtle">Nessun risultato</p>
          )}
          {filtered.map((item, i) => (
            <button
              key={item.id}
              onClick={() => select(item)}
              onMouseEnter={() => setActive(i)}
              className={[
                'w-full flex items-center justify-between px-4 py-2.5 text-left text-sm transition-colors',
                i === active ? 'bg-theme-bg-subtle text-theme-text' : 'text-theme-text-muted',
              ].join(' ')}
            >
              <span>{item.label}</span>
              {item.hint && <span className="text-xs text-theme-text-subtle">{item.hint}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
